import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Plus, Minus } from 'lucide-react';

const L = {
  es: {
    heading: 'Perfil de Dinámica Laboral — Modelo P4 de Praxora',
    headingGeneric: 'Perfil Dimensional Descriptivo',
    intensity: 'Intensidad (0–100 · descriptiva, no evaluativa)',
    config: 'Configuración integrada',
    model: 'Descripción del Modelo P4', profile: 'Perfil general P4',
    modelGeneric: 'Descripción del modelo', profileGeneric: 'Perfil general',
    dom: 'Tendencia predominante', sec: 'Tendencia secundaria',
    dec: 'Toma de decisiones', com: 'Comunicación', rit: 'Ritmo y organización',
    pre: 'Respuesta ante presión y cambio', col: 'Colaboración',
    impl: 'Implicaciones en el trabajo',
    fort: 'Fortalezas potenciales del patrón', exc: 'Posibles excesos por sobreutilización',
    excGeneric: 'Aspectos a observar', fortGeneric: 'Fortalezas potenciales',
    rec: 'Recomendaciones de desarrollo', met: 'Nota metodológica',
    lim: 'Limitaciones de interpretación',
    limGeneric: 'Alcance y limitaciones de interpretación',
    noJudgement: 'Este es un inventario de estilo: no existen respuestas correctas ni incorrectas, ni puntajes de desempeño o idoneidad.',
    editHint: 'Revisión humana: puede editar las secciones narrativas. Los puntajes dimensionales, la nota metodológica y el alcance no son editables.',
    add: 'Agregar',
  },
  en: {
    heading: 'Workplace Dynamics Profile — Praxora P4 Model',
    headingGeneric: 'Descriptive Dimensional Profile',
    intensity: 'Intensity (0–100 · descriptive, not evaluative)',
    config: 'Integrated configuration',
    model: 'The P4 Model', profile: 'Overall P4 Profile',
    modelGeneric: 'Model description', profileGeneric: 'Overall profile',
    dom: 'Dominant tendency', sec: 'Secondary tendency',
    dec: 'Decision-making', com: 'Communication', rit: 'Pace and organization',
    pre: 'Response to pressure and change', col: 'Collaboration',
    impl: 'Implications at work',
    fort: 'Potential strengths of the pattern', exc: 'Possible over-use risks',
    excGeneric: 'Aspects to monitor', fortGeneric: 'Potential strengths',
    rec: 'Development recommendations', met: 'Methodological note',
    lim: 'Interpretation limits',
    limGeneric: 'Scope and limits of interpretation',
    noJudgement: 'This is a style inventory: there are no right or wrong answers and no performance or suitability scores.',
    editHint: 'Human review: you may edit the narrative sections. Dimensional scores, the methodological note and the scope section are not editable.',
    add: 'Add',
  },
};

// Client-facing canonical texts for GENERIC dimensional instruments —
// mirrors services/praxora_p4.dimensional_client_texts (Founder UAT 2026-06:
// no internal scoring mechanics in client reports; new scope wording).
const clientTexts = (instrumentName, lang) => {
  const name = (instrumentName || '').trim();
  const sostiva = name.toLowerCase().includes('sostiva');
  if (lang === 'es') {
    const display = name || 'Este instrumento';
    const purpose = sostiva
      ? 'patrones asociados con la resiliencia y la recuperación en el contexto laboral'
      : 'patrones descriptivos de estilo en el contexto laboral';
    return {
      nota: `${display} es un instrumento autodescriptivo desarrollado para explorar ${purpose}. Sus resultados se presentan mediante dimensiones descriptivas y deben interpretarse de manera integrada, considerando las condiciones y el contexto en que fue realizada la evaluación. Los resultados no constituyen por sí solos una medición de desempeño, aptitud profesional ni idoneidad para un puesto específico.`,
      alcance: 'Los resultados describen tendencias autopercibidas relacionadas específicamente con el contexto laboral explorado por el instrumento al momento de la evaluación. Su interpretación debe limitarse a dicho ámbito y considerar que estas tendencias pueden variar ante cambios en las circunstancias, responsabilidades o entorno de trabajo. Los resultados constituyen una fuente complementaria de información y deben analizarse junto con otros antecedentes pertinentes cuando se utilicen para apoyar procesos de gestión y desarrollo del talento.',
    };
  }
  const display = name || 'This instrument';
  const purpose = sostiva
    ? 'patterns associated with resilience and recovery in the workplace'
    : 'descriptive workplace style patterns';
  return {
    nota: `${display} is a self-descriptive instrument developed to explore ${purpose}. Its results are presented through descriptive dimensions and should be interpreted in an integrated manner, considering the conditions and context in which the assessment was completed. On their own, the results do not constitute a measure of performance, professional aptitude or suitability for a specific position.`,
    alcance: 'The results describe self-perceived tendencies specifically related to the workplace context explored by the instrument at the time of the assessment. Their interpretation should remain within that scope and consider that these tendencies may vary with changes in circumstances, responsibilities or work environment. The results are a complementary source of information and should be analysed together with other relevant background when used to support talent management and development processes.',
  };
};

const Block = ({ title, text, editable, onChange, testid }) => {
  if (!editable && !text) return null;
  return (
    <div data-testid={testid}>
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">{title}</p>
      {editable ? (
        <Textarea
          value={text || ''}
          onChange={(e) => onChange(e.target.value)}
          className="text-sm leading-relaxed min-h-[96px]"
          data-testid={testid ? `${testid}-input` : undefined}
        />
      ) : (
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{text}</p>
      )}
    </div>
  );
};

const ListBlock = ({ title, items, editable, onChange, addLabel, testid }) => {
  const list = items || [];
  if (!editable && !list.length) return null;
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{title}</p>
        {editable && (
          <Button size="sm" variant="ghost" className="h-6 gap-1 text-xs"
            onClick={() => onChange([...list, ''])} data-testid={testid ? `${testid}-add` : undefined}>
            <Plus className="h-3 w-3" /> {addLabel}
          </Button>
        )}
      </div>
      {editable ? (
        <div className="space-y-2">
          {list.map((it, i) => (
            <div key={i} className="flex gap-2 items-start">
              <Textarea value={it} className="text-sm flex-1 min-h-[56px]"
                onChange={(e) => { const next = [...list]; next[i] = e.target.value; onChange(next); }} />
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                onClick={() => onChange(list.filter((_, idx) => idx !== i))}>
                <Minus className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <ul className="text-sm leading-relaxed list-disc pl-5 space-y-1">
          {list.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      )}
    </div>
  );
};

export const PraxoraP4Panel = ({ p4, mode = 'praxora_p4', editable = false, onNarrativeChange, instrumentName }) => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'es').startsWith('es') ? 'es' : 'en';
  const t4 = L[lang];
  if (!p4) return null;
  const isP4 = mode === 'praxora_p4';
  const heading = isP4 ? t4.heading : t4.headingGeneric;
  const profile = p4.profile || {};
  const n = p4.narrative || {};
  const dims = profile.dimensions || [];
  const generic = !isP4 ? clientTexts(instrumentName, lang) : null;
  const scrub = (txt) => (txt || '')
    .replace(' de H&M Advisory', '')
    .replace(' qualified H&M Advisory consultant', ' qualified consultant')
    .replace(/H&M Advisory/g, 'Corporate Talent Hub');
  const notaText = isP4 ? scrub(p4.nota_metodologica) : generic.nota;
  const limText = isP4 ? scrub(p4.limitaciones) : generic.alcance;
  const set = (key) => (val) => onNarrativeChange && onNarrativeChange(key, val);

  return (
    <div className="space-y-4" data-testid="p4-report-panel">
      <Card className="p-6 shadow-card space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h3 className="font-display font-bold text-lg">{heading}</h3>
          <Badge variant="outline" className="border-primary/30 text-primary" data-testid="p4-config-badge">
            {t4.config}: {profile.configuration || '—'}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground italic">{t4.noJudgement}</p>
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">{t4.intensity}</p>
          <div className="space-y-2.5">
            {dims.map((d) => (
              <div key={d.code} className="flex items-center gap-3" data-testid={`p4-dim-${d.code}`}>
                <span className="text-sm font-medium w-52 shrink-0 leading-tight">{d.name}</span>
                <Progress value={d.score ?? 0} className="h-2 flex-1" />
                <span className="text-sm font-semibold tabular-nums w-16 text-right">{d.score != null ? `${d.score}/100` : '—'}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>
      {editable && (
        <div className="rounded-md border border-info/40 bg-info/5 px-3 py-2 text-xs text-foreground/80" data-testid="p4-edit-hint">
          {t4.editHint}
        </div>
      )}
      <Card className="p-6 shadow-card space-y-4">
        <Block title={isP4 ? t4.model : t4.modelGeneric} text={p4.model_description} />
        <Block title={isP4 ? t4.profile : t4.profileGeneric} text={n.perfil_general}
          editable={editable} onChange={set('perfil_general')} testid="p4-narr-perfil_general" />
        {dims.map((d) => (
          <Block key={d.code} title={d.name} text={n[d.code]}
            editable={editable} onChange={set(d.code)} testid={`p4-narr-${d.code}`} />
        ))}
        <Block title={t4.dom} text={n.tendencia_predominante} editable={editable} onChange={set('tendencia_predominante')} testid="p4-narr-dom" />
        <Block title={t4.sec} text={n.tendencia_secundaria} editable={editable} onChange={set('tendencia_secundaria')} testid="p4-narr-sec" />
        <Block title={t4.config} text={n.configuracion_integrada} editable={editable} onChange={set('configuracion_integrada')} testid="p4-narr-config" />
        <Block title={t4.dec} text={n.toma_decisiones} editable={editable} onChange={set('toma_decisiones')} testid="p4-narr-dec" />
        <Block title={t4.com} text={n.comunicacion} editable={editable} onChange={set('comunicacion')} testid="p4-narr-com" />
        <Block title={t4.rit} text={n.ritmo_organizacion} editable={editable} onChange={set('ritmo_organizacion')} testid="p4-narr-rit" />
        <Block title={t4.pre} text={n.presion_cambio} editable={editable} onChange={set('presion_cambio')} testid="p4-narr-pre" />
        <Block title={t4.col} text={n.colaboracion} editable={editable} onChange={set('colaboracion')} testid="p4-narr-col" />
        <Block title={t4.impl} text={n.implicaciones_trabajo} editable={editable} onChange={set('implicaciones_trabajo')} testid="p4-narr-impl" />
        <ListBlock title={isP4 ? t4.fort : t4.fortGeneric} items={n.fortalezas} editable={editable} onChange={set('fortalezas')} addLabel={t4.add} testid="p4-narr-fortalezas" />
        <ListBlock title={isP4 ? t4.exc : t4.excGeneric} items={n.excesos} editable={editable} onChange={set('excesos')} addLabel={t4.add} testid="p4-narr-excesos" />
        <ListBlock title={t4.rec} items={n.recomendaciones} editable={editable} onChange={set('recomendaciones')} addLabel={t4.add} testid="p4-narr-recomendaciones" />
        <Block title={t4.met} text={notaText} testid="p4-nota" />
        <Block title={isP4 ? t4.lim : t4.limGeneric} text={limText} testid="p4-limitaciones" />
      </Card>
    </div>
  );
};
